import type { ObjectId, Override } from "@bbj-types/utils";
import { useSuspense } from "@root/api/tools/axios";
import { useCallback } from "react";
import { interviewExitAxiosInstance, useAxiosInterviewExit } from "../axios";
import type { IGetInterviewExitDraft } from "./draft/get";
import type { IDynamicFormSchema, IInterviewExit } from "./types";

export interface IGetInterviewExit extends Omit<IInterviewExit, "challenges" | "candidateFields"> {
  challenges: Override<
    IInterviewExit["challenges"][number],
    {
      challenge: { _id: ObjectId; name: string; icon: string };
      objectives: Override<
        IInterviewExit["challenges"][number]["objectives"][number],
        { objective: { _id: ObjectId; name: string } }
      >[];
    }
  >[];
  candidateFields: Override<
    IInterviewExit["candidateFields"],
    {
      coachingStep: IDynamicFormSchema;
    }
  >;
}

export const useGetInterviewExit = () => {
  const [state, execute, cancel] = useAxiosInterviewExit<IGetInterviewExit>(
    {
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: ObjectId, interviewExitId: ObjectId) =>
      execute({
        url: `/candidates/${candidateId}/interviews-exit/${interviewExitId}`,
      }),
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetInterviewExitSuspense = (candidateId: ObjectId, interviewExitId: ObjectId) =>
  useSuspense(
    () =>
      interviewExitAxiosInstance.get<IGetInterviewExitDraft>(
        `/candidates/${candidateId}/interviews-exit/${interviewExitId}`,
        {
          withCredentials: true,
        }
      ),
    [candidateId, interviewExitId]
  );
